// src/pages/AdminIntelbrasProdutosList.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, updateDoc, query, writeBatch } from 'firebase/firestore'; // Importar writeBatch
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../components/AdminModals.css';
import './AdminIntelbrasProdutosList.css';

const AdminIntelbrasProdutosList = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(null);
    const fileInputRef = useRef(null);
    
    // Estados para filtro, paginação, SELEÇÃO e ORDENAÇÃO
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ unidade: '', familia: '' });
    const [sortOrder, setSortOrder] = useState('nomeAsc'); // NOVO ESTADO PARA ORDENAÇÃO
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage, setProductsPerPage] = useState(10);
    const [selectedProducts, setSelectedProducts] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Removido o orderBy daqui para a ordenação ser controlada no frontend
            const prodPath = "pts-com-br/fabricantes/intelbras_products";
            const prodQuery = query(collection(db, prodPath));
            const prodSnapshot = await getDocs(prodQuery);
            const productsData = prodSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllProducts(productsData);
        } catch (err) {
            console.error("Erro ao buscar produtos Intelbras:", err);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({...prev, [name]: value}));
        setCurrentPage(1);
    };
    
    const unidades = useMemo(() => [...new Set(allProducts.map(p => p.unidade).filter(Boolean))].sort(), [allProducts]);
    const familias = useMemo(() => [...new Set(allProducts.map(p => p.familia).filter(Boolean))].sort(), [allProducts]);

    // Lógica de filtragem E ORDENAÇÃO combinada
    const filteredProducts = useMemo(() => {
        // Copia os produtos para não alterar o estado original
        let processableProducts = [...allProducts];

        // 1. APLICA A ORDENAÇÃO PRIMEIRO
        if (sortOrder === 'priceAsc') {
            processableProducts.sort((a, b) => (a.precoSugerido || 0) - (b.precoSugerido || 0));
        } else if (sortOrder === 'priceDesc') {
            processableProducts.sort((a, b) => (b.precoSugerido || 0) - (a.precoSugerido || 0));
        } else { // 'nomeAsc' é o padrão
            processableProducts.sort((a, b) => a.nome.localeCompare(b.nome));
        }

        // 2. DEPOIS APLICA A FILTRAGEM
        return processableProducts.filter(p => {
            const searchMatch = searchTerm === '' || 
                p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.ean?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const unidadeMatch = filters.unidade === '' || p.unidade === filters.unidade;
            const familiaMatch = filters.familia === '' || p.familia === filters.familia;

            return searchMatch && unidadeMatch && familiaMatch;
        });
    }, [allProducts, searchTerm, filters, sortOrder]); // Adicionado sortOrder como dependência

    const paginatedProducts = useMemo(() => {
        const indexOfLastProduct = currentPage * productsPerPage;
        const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
        return filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    }, [filteredProducts, currentPage, productsPerPage]);

    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    const handleDirectImageUpload = async (e, productToUpdate) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(productToUpdate.id);
        const storageRef = ref(storage, `intelbras/products/${productToUpdate.sku}.${file.name.split('.').pop()}`);
        
        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            const docRef = doc(db, "pts-com-br/fabricantes/intelbras_products", productToUpdate.id);
            await updateDoc(docRef, { mainImage: downloadURL });
            setAllProducts(prevProducts => prevProducts.map(p => 
                p.id === productToUpdate.id ? { ...p, mainImage: downloadURL } : p
            ));
        } catch (error) {
            console.error("Erro no upload direto da imagem:", error);
            alert("Erro ao fazer upload da imagem.");
        } finally {
            setIsUploading(null);
            fileInputRef.current.value = "";
        }
    };
    
    // --- LÓGICA DE SELEÇÃO ---
    const handleSelectProduct = (productId) => {
        setSelectedProducts(prev => 
            prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
        );
    };

    const handleSelectAllOnPage = () => {
        const pageProductIds = paginatedProducts.map(p => p.id);
        const allSelectedOnPage = pageProductIds.every(id => selectedProducts.includes(id));

        if (allSelectedOnPage) {
            setSelectedProducts(prev => prev.filter(id => !pageProductIds.includes(id)));
        } else {
            setSelectedProducts(prev => [...new Set([...prev, ...pageProductIds])]);
        }
    };
    
    // --- FUNÇÃO PARA APAGAR SELECIONADOS ---
    const handleDeleteSelected = async () => {
        if (selectedProducts.length === 0) {
            alert("Nenhum produto selecionado.");
            return;
        }
        if (window.confirm(`Tem certeza que deseja apagar ${selectedProducts.length} produto(s) selecionado(s)?`)) {
            try {
                const batch = writeBatch(db);
                selectedProducts.forEach(productId => {
                    const docRef = doc(db, "pts-com-br/fabricantes/intelbras_products", productId);
                    batch.delete(docRef);
                });
                await batch.commit();
                fetchData(); // Recarrega os dados
                setSelectedProducts([]); // Limpa a seleção
            } catch (err) {
                console.error("Erro ao apagar produtos em massa:", err);
                alert("Ocorreu um erro ao apagar os produtos.");
            }
        }
    };
    
    const formatPrice = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    return (
        <div className="admin-list-container">
            <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept="image/*" 
                onChange={(e) => {
                    const product = fileInputRef.current.productToUpdate;
                    if (product) handleDirectImageUpload(e, product);
                }}
            />
            <div className="admin-list-header">
                <h1>Gerenciamento de Produtos Intelbras</h1>
                {selectedProducts.length > 0 && (
                    <button onClick={handleDeleteSelected} className="action-button delete-button bulk-delete-btn">
                        Apagar Selecionados ({selectedProducts.length})
                    </button>
                )}
            </div>

            <div className="filters-toolbar">
                <input
                    type="text"
                    placeholder="Buscar por Nome, SKU, EAN..."
                    value={searchTerm}
                    onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
                    className="search-input-admin"
                />
                <select name="unidade" value={filters.unidade} onChange={handleFilterChange}>
                    <option value="">Todas as Unidades</option>
                    {unidades.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                <select name="familia" value={filters.familia} onChange={handleFilterChange}>
                    <option value="">Todas as Famílias</option>
                    {familias.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                {/* NOVO SELETOR DE ORDENAÇÃO */}
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                    <option value="nomeAsc">Ordenar por Nome (A-Z)</option>
                    <option value="priceAsc">Ordenar por Preço (Menor > Maior)</option>
                    <option value="priceDesc">Ordenar por Preço (Maior > Menor)</option>
                </select>
                 <select value={productsPerPage} onChange={e => {setProductsPerPage(Number(e.target.value)); setCurrentPage(1);}}>
                    <option value="10">10 por pág.</option>
                    <option value="20">20 por pág.</option>
                    <option value="30">30 por pág.</option>
                    <option value="40">40 por pág.</option>
                    <option value="50">50 por pág.</option>
                </select>
            </div>

            {loading ? <p>Carregando...</p> : (
                <>
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th className="checkbox-col">
                                    <input 
                                        type="checkbox"
                                        onChange={handleSelectAllOnPage}
                                        checked={paginatedProducts.length > 0 && paginatedProducts.every(p => selectedProducts.includes(p.id))}
                                    />
                                </th>
                                <th className="image-col">Imagem</th>
                                <th>Produto</th>
                                <th>SKU</th>
                                <th>Família</th>
                                <th>Preço Sugerido</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedProducts.map(prod => (
                                <tr key={prod.id} className={selectedProducts.includes(prod.id) ? 'selected-row' : ''}>
                                    <td className="checkbox-col">
                                        <input 
                                            type="checkbox"
                                            checked={selectedProducts.includes(prod.id)}
                                            onChange={() => handleSelectProduct(prod.id)}
                                        />
                                    </td>
                                    <td className="image-col">
                                        <button 
                                            className="image-upload-btn"
                                            onClick={() => {
                                                fileInputRef.current.productToUpdate = prod;
                                                fileInputRef.current.click();
                                            }}
                                            disabled={isUploading === prod.id}
                                        >
                                            {isUploading === prod.id ? 
                                                <div className="loader"></div> :
                                                <img src={prod.mainImage} alt={prod.nome} className="product-thumbnail" />
                                            }
                                        </button>
                                    </td>
                                    <td>{prod.nome}</td>
                                    <td>{prod.sku}</td>
                                    <td>{prod.familia}</td>
                                    <td>{formatPrice(prod.precoSugerido)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                 {totalPages > 1 && (
                    <div className="pagination-nav-admin">
                        <button onClick={() => setCurrentPage(c => Math.max(1, c - 1))} disabled={currentPage === 1}>
                            Anterior
                        </button>
                        <span>Página {currentPage} de {totalPages}</span>
                        <button onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))} disabled={currentPage === totalPages}>
                            Próxima
                        </button>
                    </div>
                )}
                </>
            )}
        </div>
    );
};

export default AdminIntelbrasProdutosList;